
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Newspaper, Download, Image, RefreshCw, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Slide {
  title: string;
  content: string[];
  imagePrompt?: string;
  imageUrl?: string;
}

interface CurrentAffairsData {
  slides: Slide[];
  provider?: string;
}

const sources = [
  { value: "pib", label: "Press Information Bureau (PIB)" },
  { value: "air", label: "All India Radio News" },
  { value: "dd", label: "Doordarshan News" },
  { value: "gov", label: "Government Portals" }
];

const topics = [
  { value: "general", label: "General & Mixed" },
  { value: "economy", label: "Economy & Finance" },
  { value: "science", label: "Science & Technology" },
  { value: "polity", label: "Polity & Governance" },
  { value: "environment", label: "Environment & Climate" },
  { value: "international", label: "International Relations" }
];

const CurrentAffairsSlides: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState<boolean>(false);
  const [selectedSource, setSelectedSource] = useState<string>("pib");
  const [selectedTopic, setSelectedTopic] = useState<string>("general");
  const [currentAffairsData, setCurrentAffairsData] = useState<CurrentAffairsData | null>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<string>("preview");
  
  // Get the current date formatted for display
  const today = new Date().toLocaleDateString('en-IN', {
    day: 'numeric', 
    month: 'long', 
    year: 'numeric'
  });

  const fetchCurrentAffairs = async () => {
    setIsLoading(true);
    try {
      const sourceLabel = sources.find(s => s.value === selectedSource)?.label || selectedSource;
      const topicLabel = topics.find(t => t.value === selectedTopic)?.label || selectedTopic;
      
      // Create prompt for fetching and summarizing current affairs
      const prompt = `Fetch and summarize today's (${today}) important current affairs news from ${sourceLabel} focusing on ${topicLabel} topics. Create a comprehensive and educational presentation with accurate facts only from reliable official sources.`;
      
      toast.info("Generating current affairs slides. This may take a moment...");
      
      // Call the generate-ppt edge function to create slides
      const { data, error } = await supabase.functions.invoke('generate-ppt', {
        body: { text: prompt }
      });
      
      if (error) {
        throw error;
      }
      
      if (data && data.slides && data.slides.length > 0) {
        setCurrentAffairsData(data);
        setCurrentSlideIndex(0);
        toast.success(`Generated ${data.slides.length} current affairs slides successfully!`);
        
        // Log the AI provider used for analytics
        console.log(`Current affairs generated using ${data.provider || 'unknown'} provider`);
      } else {
        toast.error("Failed to generate current affairs slides");
      }
    } catch (error) {
      console.error("Error fetching current affairs:", error);
      toast.error("Failed to generate current affairs: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setIsLoading(false);
    }
  };

  const generateSlideImage = async (imagePrompt: string, slideIndex: number) => {
    setIsGeneratingImage(true);
    
    try {
      // Call the generate-slide-image edge function
      const { data, error } = await supabase.functions.invoke('generate-slide-image', {
        body: { prompt: imagePrompt }
      });
      
      if (error) {
        throw error;
      }
      
      if (data && data.image) {
        // Update the slide with the generated image
        setCurrentAffairsData(prevData => {
          if (!prevData) return null;
          
          const updatedSlides = [...prevData.slides];
          updatedSlides[slideIndex] = {
            ...updatedSlides[slideIndex],
            imageUrl: data.image
          };
          
          return {
            ...prevData,
            slides: updatedSlides
          };
        });
        
        toast.success("Slide image generated successfully!");
      } else {
        toast.error("Failed to generate slide image");
      }
    } catch (error) {
      console.error("Error generating slide image:", error);
      toast.error("Failed to generate image: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleNextSlide = () => {
    if (currentAffairsData && currentSlideIndex < currentAffairsData.slides.length - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };

  const handlePrevSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  const exportToHTML = () => {
    if (!currentAffairsData) return;
    
    // Create HTML content
    let htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Current Affairs - ${today}</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
          h1 { color: #1d4ed8; }
          h2 { color: #1e40af; margin-top: 30px; }
          ul { margin-top: 15px; }
          li { margin-bottom: 8px; }
          .image-container { margin: 20px 0; text-align: center; }
          img { max-width: 100%; border-radius: 8px; }
          .footer { margin-top: 40px; text-align: center; color: #6b7280; font-size: 0.8em; }
        </style>
      </head>
      <body>
        <h1>Current Affairs - ${today}</h1>
    `;
    
    // Add each slide
    currentAffairsData.slides.forEach(slide => {
      htmlContent += `
        <h2>${slide.title}</h2>
        <ul>
          ${slide.content.map(point => `<li>${point}</li>`).join('')}
        </ul>
        ${slide.imageUrl ? `
          <div class="image-container">
            <img src="${slide.imageUrl}" alt="${slide.title}" />
          </div>
        ` : ''}
      `;
    });
    
    // Add footer
    htmlContent += `
        <div class="footer">
          <p>Generated by Edaptica - ${today}</p>
        </div>
      </body>
      </html>
    `;
    
    // Create a download link
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Current-Affairs-${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Current affairs exported as HTML");
  };

  // Get current slide
  const currentSlide = currentAffairsData?.slides[currentSlideIndex];
  
  return (
    <Card className="w-full mx-auto shadow-lg border-0 overflow-hidden animate-fadeIn">
      <CardHeader className="pb-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <CardTitle className="text-lg flex items-center">
          <Newspaper className="h-5 w-5 mr-2 text-white" />
          AI Current Affairs Slides
        </CardTitle>
      </CardHeader>
      <CardContent className="bg-gradient-to-b from-white to-blue-50">
        <div className="flex flex-col space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 block">Source</label>
              <Select
                value={selectedSource}
                onValueChange={setSelectedSource}
                disabled={isLoading}
              >
                <SelectTrigger className="bg-white border border-gray-200 hover:border-indigo-300 transition-colors">
                  <SelectValue placeholder="Select a source" />
                </SelectTrigger>
                <SelectContent>
                  {sources.map((source) => (
                    <SelectItem key={source.value} value={source.value}>
                      {source.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 block">Topic Focus</label>
              <Select
                value={selectedTopic}
                onValueChange={setSelectedTopic}
                disabled={isLoading}
              >
                <SelectTrigger className="bg-white border border-gray-200 hover:border-indigo-300 transition-colors">
                  <SelectValue placeholder="Select a topic" />
                </SelectTrigger>
                <SelectContent>
                  {topics.map((topic) => (
                    <SelectItem key={topic.value} value={topic.value}>
                      {topic.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end">
              <Button
                onClick={fetchCurrentAffairs}
                disabled={isLoading}
                className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {currentAffairsData && currentAffairsData.slides.length > 0 ? (
            <div className="mt-6">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="animate-fadeIn">
                <TabsList className="grid w-full grid-cols-2 bg-indigo-100 p-1">
                  <TabsTrigger value="preview" className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm">Slide Preview</TabsTrigger>
                  <TabsTrigger value="export" className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm">Export Options</TabsTrigger>
                </TabsList>
                
                <TabsContent value="preview" className="animate-fadeIn">
                  <div className="border rounded-lg p-4 mt-4 bg-white shadow-sm hover:shadow-md transition-all">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm text-indigo-600 font-medium bg-indigo-50 px-3 py-1 rounded-full">
                        Slide {currentSlideIndex + 1} of {currentAffairsData.slides.length}
                      </span>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={handlePrevSlide}
                          disabled={currentSlideIndex === 0}
                          className="hover:bg-indigo-50"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={handleNextSlide}
                          disabled={currentSlideIndex === currentAffairsData.slides.length - 1}
                          className="hover:bg-indigo-50"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {currentSlide && (
                      <div className="space-y-4 animate-fadeIn">
                        <h3 className="text-xl font-bold text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-transparent bg-clip-text">{currentSlide.title}</h3>
                        
                        <ul className="space-y-2 list-disc pl-5">
                          {currentSlide.content.map((point, idx) => (
                            <li key={idx} className="text-gray-700">{point}</li>
                          ))}
                        </ul>
                        
                        {currentSlide.imageUrl ? (
                          <div className="mt-4 shadow-md rounded-lg overflow-hidden transition-all hover:shadow-lg">
                            <img 
                              src={currentSlide.imageUrl} 
                              alt={currentSlide.title}
                              className="mx-auto rounded-md max-h-72 object-contain w-full"
                            />
                          </div>
                        ) : currentSlide.imagePrompt ? (
                          <div className="flex justify-center mt-4">
                            <Button
                              variant="outline"
                              onClick={() => generateSlideImage(currentSlide.imagePrompt!, currentSlideIndex)}
                              disabled={isGeneratingImage}
                              className="flex items-center gap-2 hover:bg-indigo-50"
                            >
                              {isGeneratingImage ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Generating Image...
                                </>
                              ) : (
                                <>
                                  <Image className="h-4 w-4" />
                                  Generate Image for Slide
                                </>
                              )}
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="export" className="animate-fadeIn">
                  <div className="border rounded-lg p-6 mt-4 bg-gradient-to-b from-white to-blue-50 shadow-sm hover:shadow-md transition-all">
                    <div className="flex flex-col gap-4">
                      <h3 className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Export Options</h3>
                      
                      <div className="flex flex-col gap-3">
                        <Button 
                          variant="gradient" 
                          onClick={exportToHTML}
                          className="flex items-center gap-2 w-full sm:w-auto"
                        >
                          <Download className="h-4 w-4" />
                          Export as HTML
                        </Button>
                        
                        <div className="text-sm bg-blue-50 p-4 rounded-lg flex items-start gap-2 mt-2 border border-blue-100">
                          <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-500" />
                          <p className="text-blue-800">
                            The HTML export includes all slides with their content and any generated 
                            images. You can open this file in any web browser.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          ) : !isLoading && (
            <div className="border rounded-lg p-8 mt-4 text-center bg-gradient-to-b from-white to-blue-50 shadow-md animate-fadeIn">
              <div className="bg-indigo-50 p-8 rounded-xl inline-flex flex-col items-center mx-auto max-w-md">
                <Newspaper className="h-16 w-16 text-indigo-300 mb-4" />
                <h3 className="mt-2 text-xl font-medium text-indigo-700">No Current Affairs Slides Yet</h3>
                <p className="mt-2 text-sm text-indigo-500">
                  Select a source and topic, then click "Generate" to create AI-powered current affairs slides.
                </p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CurrentAffairsSlides;
